<template>
  <div class="quiz-taking">
    <!-- No Active Quiz -->
    <div v-if="!activeQuiz" class="no-quiz">
      <div class="text-center py-4">
        <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
        <h6 class="text-muted">No active quiz</h6>
        <p class="text-muted">Wait for your teacher to start a quiz.</p>
      </div>
    </div>

    <!-- Quiz Available but not started -->
    <div v-else-if="activeQuiz.status === 'created'" class="quiz-waiting">
      <div class="quiz-preview">
        <div class="text-center py-4">
          <feather-icon icon="ClockIcon" size="48" class="text-primary mb-3" />
          <h5>{{ activeQuiz.title }}</h5>
          <p class="text-muted">Quiz is ready. Wait for your teacher to start it.</p>

          <div class="quiz-info mt-3">
            <div class="row justify-content-center">
              <div class="col-auto">
                <div class="info-card">
                  <feather-icon icon="FileTextIcon" size="20" class="mb-1" />
                  <div>{{ activeQuiz.questions_count }} Questions</div>
                </div>
              </div>
              <div v-if="activeQuiz.time_limit_minutes" class="col-auto">
                <div class="info-card">
                  <feather-icon icon="ClockIcon" size="20" class="mb-1" />
                  <div>{{ activeQuiz.time_limit_minutes }} Minutes</div>
                </div>
              </div>
              <div class="col-auto">
                <div class="info-card">
                  <feather-icon icon="RepeatIcon" size="20" class="mb-1" />
                  <div>{{ getAttemptsText(activeQuiz.attempts_allowed) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quiz Ended - Show Results -->
    <div v-else-if="activeQuiz.status === 'ended'" class="quiz-ended">
      <div v-if="userSubmission" class="quiz-results">
        <div class="results-header text-center py-4">
          <feather-icon
            :icon="userSubmission.score >= 70 ? 'CheckCircleIcon' : 'XCircleIcon'"
            size="48"
            :class="userSubmission.score >= 70 ? 'text-success' : 'text-danger'"
            class="mb-3"
          />
          <h5>Quiz Completed!</h5>
          <p class="text-muted">Here are your results:</p>
        </div>

        <div class="score-card">
          <div class="score-display">
            <div class="score-number">{{ Math.round(userSubmission.score) }}%</div>
            <div class="score-fraction">{{ userSubmission.correct_answers }}/{{ userSubmission.total_questions }} correct</div>
          </div>
          <div class="score-details">
            <div class="detail-item">
              <span class="detail-label">Time Spent:</span>
              <span class="detail-value">{{ formatDuration(userSubmission.time_spent) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Submitted:</span>
              <span class="detail-value">{{ formatDateTime(userSubmission.submitted_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Show individual answers if enabled -->
        <div v-if="activeQuiz.show_results_immediately && quizData" class="answer-review mt-4">
          <h6>Answer Review</h6>
          <div
            v-for="(question, index) in quizData.questions"
            :key="question.id"
            class="answer-card"
          >
            <div class="answer-header">
              <span class="question-number">{{ index + 1 }}</span>
              <span class="question-status">
                <feather-icon
                  :icon="isQuestionCorrect(question.id) ? 'CheckIcon' : 'XIcon'"
                  size="16"
                  :class="isQuestionCorrect(question.id) ? 'text-success' : 'text-danger'"
                />
              </span>
            </div>
            <div class="question-text">{{ question.title }}</div>
            <div class="answers-list">
              <div
                v-for="answer in question.answers"
                :key="answer.id"
                class="answer-option"
                :class="{
                  'correct': answer.is_correct,
                  'selected': isAnswerSelected(question.id, answer.id),
                  'wrong-selected': isAnswerSelected(question.id, answer.id) && !answer.is_correct
                }"
              >
                {{ answer.title }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-4">
        <feather-icon icon="AlertCircleIcon" size="48" class="text-muted mb-3" />
        <h6 class="text-muted">Quiz ended</h6>
        <p class="text-muted">You did not participate in this quiz.</p>
      </div>
    </div>

    <!-- Active Quiz - Taking Interface -->
    <div v-else-if="activeQuiz.status === 'active'" class="quiz-active">
      <!-- Already Submitted -->
      <div v-if="userSubmission && userSubmission.is_submitted" class="quiz-submitted">
        <div class="text-center py-4">
          <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-3" />
          <h5>Quiz Submitted!</h5>
          <p class="text-muted">You have successfully submitted your quiz.</p>

          <div v-if="activeQuiz.show_results_immediately" class="score-preview mt-3">
            <div class="score-badge">
              <div class="score-text">Your Score</div>
              <div class="score-value">{{ Math.round(userSubmission.score) }}%</div>
            </div>
          </div>

          <p class="text-muted mt-3">
            {{ activeQuiz.show_results_immediately ?
              'Full results will be available when the quiz ends.' :
              'Results will be available when the teacher ends the quiz.'
            }}
          </p>
        </div>
      </div>

      <!-- Quiz Taking Interface -->
      <div v-else class="quiz-interface">
        <!-- Quiz Header -->
        <div class="quiz-header">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h5 class="mb-1">{{ activeQuiz.title }}</h5>
              <span class="text-muted">
                Question {{ currentQuestionIndex + 1 }} of {{ quizData.questions.length }}
              </span>
            </div>
            <div class="quiz-timer" v-if="timeRemaining !== null">
              <feather-icon icon="ClockIcon" size="16" class="mr-1" />
              <span :class="{ 'text-danger': timeRemaining < 300 }">
                {{ formatTimeRemaining(timeRemaining) }}
              </span>
            </div>
          </div>

          <!-- Progress Bar -->
          <div class="progress mt-2">
            <div
              class="progress-bar"
              :style="{ width: ((currentQuestionIndex + 1) / quizData.questions.length) * 100 + '%' }"
            />
          </div>
        </div>

        <!-- Current Question -->
        <div v-if="currentQuestion" class="question-container">
          <div class="question-card">
            <div class="question-text">
              {{ currentQuestion.title }}
            </div>

            <div class="question-answers">
              <div
                v-for="answer in currentQuestion.answers"
                :key="answer.id"
                class="answer-option"
                @click="selectAnswer(answer.id)"
                :class="{
                  'selected': isCurrentAnswerSelected(answer.id),
                  'multiple-choice': currentQuestion.is_multiple_choice
                }"
              >
                <div class="answer-input">
                  <input
                    :type="currentQuestion.is_multiple_choice ? 'checkbox' : 'radio'"
                    :checked="isCurrentAnswerSelected(answer.id)"
                    @change="selectAnswer(answer.id)"
                    :name="'question-' + currentQuestion.id"
                  />
                </div>
                <div class="answer-text">
                  {{ answer.title }}
                </div>
              </div>
            </div>

            <div class="question-note" v-if="currentQuestion.is_multiple_choice">
              <feather-icon icon="InfoIcon" size="16" class="mr-1" />
              Select all correct answers
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="quiz-navigation">
            <b-button
              variant="outline-secondary"
              @click="previousQuestion"
              :disabled="currentQuestionIndex === 0"
            >
              <feather-icon icon="ChevronLeftIcon" size="16" class="mr-1" />
              Previous
            </b-button>

            <div class="navigation-info">
              <span class="text-muted">
                {{ answeredCount }} of {{ quizData.questions.length }} answered
              </span>
            </div>

            <b-button
              v-if="currentQuestionIndex < quizData.questions.length - 1"
              variant="primary"
              @click="nextQuestion"
            >
              Next
              <feather-icon icon="ChevronRightIcon" size="16" class="ml-1" />
            </b-button>

            <b-button
              v-else
              variant="success"
              @click="showSubmitConfirmation = true"
              :disabled="answeredCount === 0"
            >
              <feather-icon icon="SendIcon" size="16" class="mr-1" />
              Submit Quiz
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Confirmation Modal -->
    <b-modal
      v-model="showSubmitConfirmation"
      title="Submit Quiz"
      @ok="submitQuiz"
      ok-title="Submit"
      ok-variant="success"
      cancel-title="Continue Quiz"
    >
      <div class="submit-summary">
        <p>Are you sure you want to submit your quiz?</p>

        <div class="summary-stats">
          <div class="stat-item">
            <strong>{{ answeredCount }}</strong> of <strong>{{ quizData.questions.length }}</strong> questions answered
          </div>
          <div v-if="unansweredCount > 0" class="stat-item text-warning">
            <feather-icon icon="AlertTriangleIcon" size="16" class="mr-1" />
            {{ unansweredCount }} questions remain unanswered
          </div>
        </div>

        <p class="text-muted mt-3">
          You cannot change your answers after submitting.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import LessonRoomApi from '@/api/lessonRoom'

export default {
  name: 'QuizTaking',

  props: {
    webinarId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      quizData: null,
      userSubmission: null,
      currentQuestionIndex: 0,
      userAnswers: {},
      timeRemaining: null,
      timer: null,
      showSubmitConfirmation: false,
      isSubmitting: false
    }
  },

  computed: {
    ...mapState('lessonRoom', ['activeQuiz']),
    ...mapGetters('user', ['user']),

    currentQuestion() {
      return this.quizData?.questions[this.currentQuestionIndex] || null
    },

    answeredCount() {
      return Object.keys(this.userAnswers).length
    },

    unansweredCount() {
      return (this.quizData?.questions.length || 0) - this.answeredCount
    }
  },

  watch: {
    activeQuiz: {
      immediate: true,
      async handler(newQuiz) {
        if (newQuiz) {
          await this.loadQuizData()
        }
      }
    }
  },

  async created() {
    this.setupQuizEventListeners()
    if (this.activeQuiz) {
      await this.loadQuizData()
    }
  },

  beforeDestroy() {
    this.cleanupTimer()
    this.cleanupEventListeners()
  },

  methods: {
    async loadQuizData() {
      if (!this.activeQuiz) return

      try {
        // Load quiz questions for student
        if (this.activeQuiz.status === 'active') {
          const response = await LessonRoomApi.getQuizForStudent(
            this.webinarId,
            this.activeQuiz.id
          )
          this.quizData = response.data.quiz
          this.userSubmission = response.data.submission
          this.userAnswers = response.data.submission?.answers || {}

          // Start timer if quiz has time limit
          if (this.quizData.time_limit_minutes && !this.userSubmission?.is_submitted) {
            this.startTimer()
          }
        }

        // Load submission for ended quizzes
        if (this.activeQuiz.status === 'ended') {
          const response = await LessonRoomApi.getUserQuizSubmission(
            this.webinarId,
            this.activeQuiz.id
          )
          this.userSubmission = response.data.submission
          if (response.data.quiz) {
            this.quizData = response.data.quiz
          }
        }
      } catch (error) {
        console.error('Failed to load quiz data:', error)
      }
    },

    selectAnswer(answerId) {
      if (!this.currentQuestion || this.userSubmission?.is_submitted) return

      const questionId = this.currentQuestion.id

      if (this.currentQuestion.is_multiple_choice) {
        // Multiple choice - toggle answer
        if (!this.userAnswers[questionId]) {
          this.userAnswers[questionId] = []
        }

        const index = this.userAnswers[questionId].indexOf(answerId)
        if (index > -1) {
          this.userAnswers[questionId].splice(index, 1)
        } else {
          this.userAnswers[questionId].push(answerId)
        }

        // Remove empty arrays
        if (this.userAnswers[questionId].length === 0) {
          delete this.userAnswers[questionId]
        }
      } else {
        // Single choice - replace answer
        this.userAnswers[questionId] = [answerId]
      }

      // Auto-save answers
      this.saveAnswers()
    },

    isCurrentAnswerSelected(answerId) {
      const questionId = this.currentQuestion?.id
      if (!questionId || !this.userAnswers[questionId]) return false
      return this.userAnswers[questionId].includes(answerId)
    },

    isAnswerSelected(questionId, answerId) {
      if (!this.userSubmission?.answers) return false
      const answer = this.userSubmission.answers.find(a => a.question_id === questionId)
      return answer?.answer_ids?.includes(answerId) || false
    },

    isQuestionCorrect(questionId) {
      const question = this.quizData.questions.find(q => q.id === questionId)
      const userAnswer = this.userSubmission.answers.find(a => a.question_id === questionId)

      if (!question || !userAnswer) return false

      return question.validateUserAnswer(userAnswer.answer_ids || [])
    },

    nextQuestion() {
      if (this.currentQuestionIndex < this.quizData.questions.length - 1) {
        this.currentQuestionIndex++
      }
    },

    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--
      }
    },

    async saveAnswers() {
      // Save answers to backend
      try {
        await LessonRoomApi.saveQuizAnswers(
          this.webinarId,
          this.activeQuiz.id,
          { answers: this.userAnswers }
        )
      } catch (error) {
        console.error('Failed to save answers:', error)
      }
    },

    async submitQuiz() {
      if (this.isSubmitting) return

      this.isSubmitting = true
      try {
        const response = await LessonRoomApi.submitQuiz(
          this.webinarId,
          this.activeQuiz.id,
          { answers: this.userAnswers }
        )

        this.userSubmission = response.data.submission
        this.cleanupTimer()

        this.$bvToast.toast('Your quiz has been submitted successfully', {
          title: 'Quiz Submitted',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        console.error('Failed to submit quiz:', error)
        this.$bvToast.toast('Failed to submit quiz', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isSubmitting = false
        this.showSubmitConfirmation = false
      }
    },

    startTimer() {
      if (!this.quizData.time_limit_minutes) return

      const startTime = new Date(this.userSubmission?.started_at || Date.now())
      const endTime = new Date(startTime.getTime() + (this.quizData.time_limit_minutes * 60 * 1000))

      this.timer = setInterval(() => {
        const now = Date.now()
        this.timeRemaining = Math.max(0, Math.floor((endTime - now) / 1000))

        if (this.timeRemaining <= 0) {
          this.submitQuiz()
        }
      }, 1000)
    },

    cleanupTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    setupQuizEventListeners() {
      if (window.Echo) {
        this.echoChannel = window.Echo.private(`lesson-room.${this.webinarId}`)
          .listen('.quiz.started', (event) => {
            this.loadQuizData()
          })
          .listen('.quiz.ended', (event) => {
            this.cleanupTimer()
            this.loadQuizData()
          })
      }
    },

    cleanupEventListeners() {
      if (this.echoChannel) {
        this.echoChannel.stopListening('.quiz.started')
        this.echoChannel.stopListening('.quiz.ended')
      }
    },

    getAttemptsText(attempts) {
      return attempts === 0 ? 'Unlimited' : `${attempts} ${attempts === 1 ? 'Attempt' : 'Attempts'}`
    },

    formatDuration(seconds) {
      if (!seconds) return '--'
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    },

    formatTimeRemaining(seconds) {
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    },

    formatDateTime(timestamp) {
      return new Date(timestamp).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.quiz-taking {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  min-height: 400px;

  .no-quiz, .quiz-waiting, .quiz-ended {
    background: transparent;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  .quiz-preview {
    padding: 1.5rem;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
    border: 1px solid #e9ecef;

    .info-card {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 1.25rem;
      text-align: center;
      min-width: 130px;
      margin: 0 0.5rem;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
      transition: all 0.2s;

      &:hover {
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        transform: translateY(-2px);
      }

      svg {
        color: #7367f0;
      }

      div {
        font-size: 0.875rem;
        color: #2c3e50;
        font-weight: 600;
        margin-top: 0.5rem;
      }
    }
  }

  .score-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 2rem 1.5rem;
    text-align: center;
    margin: 1rem 0;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);

    .score-display {
      margin-bottom: 1.5rem;

      .score-number {
        font-size: 3.5rem;
        font-weight: 700;
        color: #7367f0;
        line-height: 1;
      }

      .score-fraction {
        color: #6c757d;
        font-size: 1.1rem;
        margin-top: 0.75rem;
        font-weight: 500;
      }
    }

    .score-details {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
      border-top: 1px solid #e9ecef;
      padding-top: 1rem;

      .detail-item {
        .detail-label {
          color: #6c757d;
          font-size: 0.875rem;
          display: block;
        }

        .detail-value {
          font-weight: 500;
          color: #495057;
        }
      }
    }
  }

  .score-badge {
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    color: white;
    padding: 1.25rem;
    border-radius: 8px;
    display: inline-block;
    box-shadow: 0 4px 8px rgba(115, 103, 240, 0.3);

    .score-text {
      font-size: 0.875rem;
      opacity: 0.95;
      font-weight: 500;
    }

    .score-value {
      font-size: 1.75rem;
      font-weight: 700;
    }
  }

  .answer-review {
    h6 {
      color: #2c3e50;
      font-weight: 600;
      margin-bottom: 1rem;
    }

    .answer-card {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 1.25rem;
      margin-bottom: 1rem;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);

      .answer-header {
        display: flex;
        align-items: center;
        justify-content: between;
        margin-bottom: 0.5rem;

        .question-number {
          background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.35rem 0.65rem;
          border-radius: 6px;
          margin-right: 0.75rem;
          box-shadow: 0 2px 4px rgba(115, 103, 240, 0.2);
        }

        .question-status {
          margin-left: auto;
        }
      }

      .question-text {
        font-weight: 500;
        color: #495057;
        margin-bottom: 1rem;
      }

      .answers-list {
        .answer-option {
          padding: 0.5rem;
          border-radius: 4px;
          margin-bottom: 0.25rem;
          background: #f8f9fa;

          &.correct {
            background: #d4edda;
            border: 1px solid #c3e6cb;
          }

          &.selected {
            background: #cce5ff;
            border: 1px solid #80bdff;
          }

          &.wrong-selected {
            background: #f5c6cb;
            border: 1px solid #e3969a;
          }
        }
      }
    }
  }

  .quiz-interface {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
    border: 1px solid #e9ecef;

    .quiz-header {
      background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
      border-bottom: 1px solid rgba(255,255,255,0.2);
      padding: 1.25rem;
      color: white;

      .quiz-timer {
        font-weight: 600;
        color: white;
        font-size: 1.1rem;
      }

      .progress {
        height: 8px;
        background-color: rgba(255,255,255,0.3);
        border-radius: 4px;

        .progress-bar {
          background-color: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      }
    }

    .question-container {
      padding: 2rem 1.5rem;
    }

    .question-card {
      .question-text {
        font-size: 1.15rem;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 1.75rem;
        line-height: 1.6;
      }

      .question-answers {
        .answer-option {
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 1.25rem;
          margin-bottom: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          background: white;

          &:hover {
            border-color: #7367f0;
            background-color: #f8f9fa;
            transform: translateX(4px);
            box-shadow: 0 2px 4px rgba(115, 103, 240, 0.1);
          }

          &.selected {
            border-color: #7367f0;
            background: linear-gradient(135deg, rgba(115, 103, 240, 0.08) 0%, rgba(158, 149, 245, 0.05) 100%);
            box-shadow: 0 2px 8px rgba(115, 103, 240, 0.15);
          }

          .answer-input {
            margin-right: 0.75rem;

            input {
              width: 18px;
              height: 18px;
              cursor: pointer;
            }
          }

          .answer-text {
            flex: 1;
            font-weight: 500;
            color: #2c3e50;
            font-size: 1rem;
          }
        }
      }

      .question-note {
        color: #6c757d;
        font-size: 0.875rem;
        display: flex;
        align-items: center;
        margin-top: 1.5rem;
        font-weight: 500;
      }
    }

    .quiz-navigation {
      display: flex;
      align-items: center;
      justify-content: between;
      margin-top: 2rem;
      padding-top: 1rem;
      border-top: 1px solid #e9ecef;

      .navigation-info {
        flex: 1;
        text-align: center;
      }
    }
  }

  .submit-summary {
    .summary-stats {
      background: #f8f9fa;
      border-radius: 6px;
      padding: 1rem;
      margin: 1rem 0;

      .stat-item {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}
</style>